import apiService from './api';

/**
 * Service API du module Pharmacie centralisée.
 * Toutes les méthodes renvoient la réponse backend ({ success, data, message }).
 */
const pharmacyService = {
  // Catalogue
  listMedications(params = {}) {
    return apiService.get('/pharmacy/medications', { params });
  },
  createMedication(payload) {
    return apiService.post('/pharmacy/medications', payload);
  },
  updateMedication(id, payload) {
    return apiService.put(`/pharmacy/medications/${id}`, payload);
  },
  listCategories() {
    return apiService.get('/pharmacy/categories');
  },

  // Stock & lots
  getCentralSite() {
    return apiService.get('/pharmacy/central-site');
  },
  setCentralSite(siteId) {
    return apiService.patch(`/pharmacy/central-site/${siteId}`, {});
  },
  getSiteStock(siteId, params = {}) {
    return apiService.get(`/pharmacy/sites/${siteId}/stock`, { params });
  },
  getMedicationLots(medicationId, params = {}) {
    return apiService.get(`/pharmacy/medications/${medicationId}/lots`, { params });
  },
  adjustLot(lotId, payload) {
    return apiService.post(`/pharmacy/lots/${lotId}/adjust`, payload);
  },
  updateLot(lotId, payload) {
    return apiService.patch(`/pharmacy/lots/${lotId}`, payload);
  },
  getStock(params = {}) {
    return apiService.get('/pharmacy/stock', { params });
  },
  listMovements(params = {}) {
    return apiService.get('/pharmacy/movements', { params });
  },
  getAlerts(params = {}) {
    return apiService.get('/pharmacy/alerts', { params });
  },

  getDutyCabinetStock(params = {}) {
    return apiService.get('/pharmacy/duty-cabinet/stock', { params });
  },
  restockDutyCabinet(payload) {
    return apiService.post('/pharmacy/duty-cabinet/restock', payload);
  },

  // Fournisseurs
  listSuppliers(params = {}) {
    return apiService.get('/pharmacy/suppliers', { params });
  },
  getSupplier(id) {
    return apiService.get(`/pharmacy/suppliers/${id}`);
  },
  createSupplier(payload) {
    return apiService.post('/pharmacy/suppliers', payload);
  },
  updateSupplier(id, payload) {
    return apiService.put(`/pharmacy/suppliers/${id}`, payload);
  },
  deleteSupplier(id) {
    return apiService.delete(`/pharmacy/suppliers/${id}`);
  },

  // Commandes fournisseurs
  listOrders(params = {}) {
    return apiService.get('/pharmacy/orders', { params });
  },
  orderStatistics() {
    return apiService.get('/pharmacy/orders/statistics');
  },
  getOrder(id) {
    return apiService.get(`/pharmacy/orders/${id}`);
  },
  createOrder(payload) {
    return apiService.post('/pharmacy/orders', payload);
  },
  submitOrder(id) {
    return apiService.patch(`/pharmacy/orders/${id}/submit`, {});
  },
  validateOrder(id) {
    return apiService.patch(`/pharmacy/orders/${id}/validate`, {});
  },
  receiveOrder(id, payload) {
    return apiService.patch(`/pharmacy/orders/${id}/receive`, payload);
  },
  cancelOrder(id) {
    return apiService.patch(`/pharmacy/orders/${id}/cancel`, {});
  },

  // Demandes d'approvisionnement
  listSupplyRequests(params = {}) {
    return apiService.get('/pharmacy/supply-requests', { params });
  },
  getSupplyRequest(id) {
    return apiService.get(`/pharmacy/supply-requests/${id}`);
  },
  createSupplyRequest(payload) {
    return apiService.post('/pharmacy/supply-requests', payload);
  },
  reviewSupplyRequest(id, payload) {
    return apiService.patch(`/pharmacy/supply-requests/${id}/review`, payload);
  },
  cancelSupplyRequest(id) {
    return apiService.patch(`/pharmacy/supply-requests/${id}/cancel`, {});
  },
  distributeSupplyRequest(id) {
    return apiService.post(`/pharmacy/supply-requests/${id}/distribute`, {});
  },

  // Distributions internes
  listDistributions(params = {}) {
    return apiService.get('/pharmacy/distributions', { params });
  },
  getDistribution(id) {
    return apiService.get(`/pharmacy/distributions/${id}`);
  },
  createDistribution(payload) {
    return apiService.post('/pharmacy/distributions', payload);
  },
  updateDistribution(id, payload) {
    return apiService.patch(`/pharmacy/distributions/${id}`, payload);
  },
  setDistributionStatus(id, status) {
    return apiService.patch(`/pharmacy/distributions/${id}/status`, { status });
  },
  deliverDistribution(id) {
    return apiService.patch(`/pharmacy/distributions/${id}/deliver`, {});
  },

  // Dispensations
  listDispensations(params = {}) {
    return apiService.get('/pharmacy/dispensations', { params });
  },
  getDispensation(id) {
    return apiService.get(`/pharmacy/dispensations/${id}`);
  },
  checkInteractions(medicationIds) {
    return apiService.post('/pharmacy/dispensations/check-interactions', { medication_ids: medicationIds });
  },
  createDispensation(payload) {
    return apiService.post('/pharmacy/dispensations', payload);
  },
  updateDispensation(id, payload) {
    return apiService.patch(`/pharmacy/dispensations/${id}`, payload);
  },
  dispense(id, payload = {}) {
    return apiService.patch(`/pharmacy/dispensations/${id}/dispense`, payload);
  },
  cancelDispensation(id) {
    return apiService.patch(`/pharmacy/dispensations/${id}/cancel`, {});
  },

  // Interactions
  listInteractions(params = {}) {
    return apiService.get('/pharmacy/interactions', params);
  },
  createInteraction(payload) {
    return apiService.post('/pharmacy/interactions', payload);
  },
  updateInteraction(id, payload) {
    return apiService.put(`/pharmacy/interactions/${id}`, payload);
  },
  deleteInteraction(id) {
    return apiService.delete(`/pharmacy/interactions/${id}`);
  },

  // Inventaire
  listInventories(params = {}) {
    return apiService.get('/pharmacy/inventories', { params });
  },
  getInventory(id) {
    return apiService.get(`/pharmacy/inventories/${id}`);
  },
  createInventory(payload) {
    return apiService.post('/pharmacy/inventories', payload);
  },
  saveInventoryLines(id, payload) {
    return apiService.patch(`/pharmacy/inventories/${id}/lines`, payload);
  },
  addInventoryLine(id, payload) {
    return apiService.post(`/pharmacy/inventories/${id}/lines`, payload);
  },
  closeInventory(id) {
    return apiService.patch(`/pharmacy/inventories/${id}/close`, {});
  },
  cancelInventory(id) {
    return apiService.patch(`/pharmacy/inventories/${id}/cancel`, {});
  },

  // Reporting
  dashboard() {
    return apiService.get('/pharmacy/reports/dashboard');
  },
  reportOverview(params = {}) {
    return apiService.get('/pharmacy/reports/overview', { params });
  }
};

export default pharmacyService;
